/* @jsx h */
import { defineComponent, h, ref, computed, watch } from "vue";
import { RightSheet, useLocalization } from "@lemmonhq/uikit";
import { getRestaurantDateKey } from "@lemmonhq/blogic";
import { RestaurantSettingKey } from "@lemmonhq/types";
import { api } from "@app/data/api";
import { useAuth } from "@app/vue/providers/Auth";
import { useToast } from "@app/vue/providers/Toast";

export interface ScheduledClosuresSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

// A pre-planned full-day closure entry in the vacation schedule.
const CLOSURE_REASON = "closed_by_manager";

export const ScheduledClosuresSheet =
  defineComponent<ScheduledClosuresSheetProps>({
    name: "ScheduledClosuresSheet",
    props: ["isOpen", "onClose"],
    setup(props) {
      const { t } = useLocalization();
      const auth = useAuth();
      const toast = useToast();

      const timezone = computed(
        () => auth.user.value?.restaurant?.timezone || "UTC",
      );
      const todayStr = computed(() =>
        getRestaurantDateKey(new Date(), timezone.value),
      );

      const closures = ref<string[]>([]);
      const loading = ref(false);
      const saving = ref(false);
      const newDate = ref("");
      // Optional note shown to guests on the customer booking page for that
      // day (same `guestNote` field the Settings → Vacation form writes).
      const newGuestNote = ref("");

      // Read the full vacation_schedule items array from settings.
      const readScheduleItems = async (): Promise<any[]> => {
        const settingsResponse = await api.getSettings();
        const vacationSetting = settingsResponse.data.find(
          (s) => s.key === RestaurantSettingKey.VACATION_SCHEDULE,
        );
        if (!vacationSetting?.value) return [];
        const parsed =
          typeof vacationSetting.value === "string"
            ? JSON.parse(vacationSetting.value)
            : vacationSetting.value;
        // Stored as { items: [...] } but may be a bare array in legacy data.
        return Array.isArray(parsed) ? parsed : parsed?.items || [];
      };

      const writeScheduleItems = async (items: any[]) => {
        await api.bulkUpdateSettings(
          {
            settings: [
              {
                key: RestaurantSettingKey.VACATION_SCHEDULE,
                value: JSON.stringify({ items }),
                value_type: "json",
              },
            ],
          },
          auth,
        );
      };

      const loadClosures = async () => {
        loading.value = true;
        try {
          const items = await readScheduleItems();
          closures.value = items
            .filter(
              (entry: any) =>
                entry.reason === CLOSURE_REASON &&
                entry.payload?.type === "closed_all_day" &&
                entry.date >= todayStr.value,
            )
            .map((entry: any) => entry.date as string)
            .sort();
        } catch (e) {
          console.error("Failed to load scheduled closures", e);
        } finally {
          loading.value = false;
        }
      };

      const addClosure = async () => {
        const date = newDate.value;
        if (!date) return;
        if (date < todayStr.value) {
          toast.showToast("Pick today or a future date.", {
            type: "error",
            autoHide: 3000,
          });
          return;
        }
        saving.value = true;
        try {
          const items = await readScheduleItems();
          // Don't duplicate a closure / existing closed-all-day entry for that date.
          const alreadyClosed = items.some(
            (entry: any) =>
              entry.date === date && entry.payload?.type === "closed_all_day",
          );
          if (!alreadyClosed) {
            const guestNote = newGuestNote.value.trim().slice(0, 500);
            items.push({
              date,
              reason: CLOSURE_REASON,
              payload: { type: "closed_all_day" },
              ...(guestNote ? { guestNote } : {}),
            });
            await writeScheduleItems(items);
          }
          newDate.value = "";
          newGuestNote.value = "";
          await loadClosures();
          toast.showToast("Closure scheduled.", {
            type: "success",
            autoHide: 3000,
          });
        } catch (e) {
          console.error("Failed to schedule closure", e);
          toast.showToast("Failed to schedule closure.", {
            type: "error",
            autoHide: 3000,
          });
        } finally {
          saving.value = false;
        }
      };

      const removeClosure = async (date: string) => {
        saving.value = true;
        try {
          const items = await readScheduleItems();
          const next = items.filter(
            (entry: any) =>
              !(entry.date === date && entry.reason === CLOSURE_REASON),
          );
          await writeScheduleItems(next);
          await loadClosures();
          toast.showToast("Closure removed.", {
            type: "success",
            autoHide: 3000,
          });
        } catch (e) {
          console.error("Failed to remove closure", e);
          toast.showToast("Failed to remove closure.", {
            type: "error",
            autoHide: 3000,
          });
        } finally {
          saving.value = false;
        }
      };

      const formatDate = (date: string) => {
        // date is YYYY-MM-DD — render without timezone shifting
        const [y, m, d] = date.split("-").map(Number);
        return new Date(y, m - 1, d).toLocaleDateString(undefined, {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      };

      watch(
        () => props.isOpen,
        (open) => {
          if (open) {
            newDate.value = "";
            newGuestNote.value = "";
            loadClosures();
          }
        },
        { immediate: true },
      );

      return () => (
        <RightSheet
          isOpen={props.isOpen}
          onClose={props.onClose}
          width="460px"
          title={
            <div class="flex items-center gap-2 text-accent">
              <span class="material-symbols-outlined text-xl text-primary">
                event_busy
              </span>
              <span class="text-lg font-semibold">Scheduled closures</span>
            </div>
          }
        >
          <div class="px-5 py-4 space-y-5">
            <p class="text-sm text-base-content/60 leading-relaxed">
              Close reservations on specific upcoming dates (e.g. a public
              holiday). Customers won't be able to book on a closed date.
            </p>

            {/* Add a closure */}
            <div class="space-y-2">
              <div class="flex items-end gap-2">
                <div class="flex-1">
                  <label class="block text-xs font-medium text-base-content/60 mb-1">
                    Add a date
                  </label>
                  <input
                    type="date"
                    class="input input-bordered input-sm w-full"
                    min={todayStr.value}
                    value={newDate.value}
                    onInput={(e) =>
                      (newDate.value = (e.target as HTMLInputElement).value)
                    }
                  />
                </div>
                <button
                  type="button"
                  class="btn btn-primary btn-sm gap-1"
                  disabled={!newDate.value || saving.value}
                  onClick={addClosure}
                >
                  <span class="material-symbols-outlined text-base">add</span>
                  Close
                </button>
              </div>
              <div>
                <label class="block text-xs font-medium text-base-content/60 mb-1">
                  {t("settings.vacation.guest_note_label")}
                </label>
                <textarea
                  class="textarea textarea-bordered textarea-sm w-full"
                  rows={2}
                  maxlength={500}
                  placeholder={t("settings.vacation.guest_note_placeholder")}
                  value={newGuestNote.value}
                  onInput={(e) =>
                    (newGuestNote.value = (
                      e.target as HTMLTextAreaElement
                    ).value)
                  }
                />
              </div>
            </div>

            {/* Existing closures */}
            <div class="space-y-2">
              <div class="text-[11px] font-semibold text-base-content/40 uppercase tracking-wider">
                Upcoming closures
              </div>
              {loading.value ? (
                <div class="flex justify-center py-6">
                  <span class="loading loading-spinner loading-md"></span>
                </div>
              ) : closures.value.length === 0 ? (
                <div class="text-sm text-base-content/40 italic py-3">
                  No upcoming closures scheduled.
                </div>
              ) : (
                <div class="space-y-1.5">
                  {closures.value.map((date) => (
                    <div
                      key={date}
                      class="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-base-200/50 border border-base-300/40"
                    >
                      <div class="flex items-center gap-2 min-w-0">
                        <span class="material-symbols-outlined text-base text-error">
                          event_busy
                        </span>
                        <span class="text-sm text-accent truncate">
                          {formatDate(date)}
                        </span>
                      </div>
                      <button
                        type="button"
                        class="btn btn-ghost btn-xs btn-square text-base-content/50 hover:text-error"
                        disabled={saving.value}
                        title="Remove closure"
                        onClick={() => removeClosure(date)}
                      >
                        <span class="material-symbols-outlined text-base">
                          close
                        </span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </RightSheet>
      );
    },
  });
